import React from 'react';
import { Form } from 'react-bootstrap';

// Qualtrics SR2: "How will peer support help this course?" — these six
// values must stay in sync with the enum on
// server/src/api/application/application.model.js's
// courseContext.supportUseCases (that file's T-AM-10 test pins the same
// six values, so a rename on either side without the other fails a test
// instead of silently drifting).
export const SUPPORT_USE_CASE_OPTIONS = [
    { value: 'lecture', label: 'Lecture / in-class sessions' },
    { value: 'lab', label: 'Lab' },
    { value: 'help_room', label: 'Help room / drop-in hours' },
    { value: 'group_sessions', label: 'Group sessions' },
    { value: 'exam_review', label: 'Exam-review sessions' },
    { value: 'other', label: 'Other' },
];

// O(1) label lookup for read-only views, built once from the array above
// instead of a .find() per rendered value.
export const SUPPORT_USE_CASE_LABELS = new Map(
    SUPPORT_USE_CASE_OPTIONS.map((opt) => [opt.value, opt.label])
);

// Mirrors the shape of Application.courseContext in
// server/src/api/application/application.model.js — the default state
// for both the create wizard and the edit/resubmit flow. A factory
// (rather than a shared object literal) so each component gets its own
// fresh object/array — nothing today mutates courseContext in place, but
// a shared reference would otherwise leak state across component
// instances the moment something did.
export function getDefaultCourseContext() {
    return {
        rationaleForSupport: '',
        supportUseCases: [],
        otherSupportUseCase: '',
        peerSupporterBenefit: '',
        additionalNotes: '',
    };
}

// Single source of truth for "what's missing from Support Rationale,"
// shared by CreateApplication.js's and ApplicationDetailPage.js's
// canProceed/error-summary so the create wizard and the edit/resubmit flow
// can't silently diverge on what counts as a valid submission. Every entry's
// `id` matches this component's DOM ids (all fixed 'ca-' ids, since this
// component isn't parameterized by idPrefix — the create and detail pages
// never mount it at the same time).
export function getCourseContextErrors(courseContext) {
    const errors = [];
    if (!courseContext?.rationaleForSupport?.trim()) {
        errors.push({
            id: 'ca-rationale-for-support',
            label: 'Rationale for Support',
        });
    }
    if (!courseContext?.supportUseCases?.length) {
        errors.push({
            id: 'ca-support-use-cases-group',
            label: 'Support use cases',
        });
    }
    if (
        courseContext?.supportUseCases?.includes('other') &&
        !courseContext?.otherSupportUseCase?.trim()
    ) {
        errors.push({
            id: 'ca-other-support-use-case',
            label: 'Other use case description',
        });
    }
    if (!courseContext?.peerSupporterBenefit?.trim()) {
        errors.push({
            id: 'ca-peer-supporter-benefit',
            label: 'Peer supporter benefit',
        });
    }
    return errors;
}

// Boolean form of getCourseContextErrors, for callers that only need "is
// this complete," not the error detail. Both wizard pages now call
// getCourseContextErrors directly (they need the detail), so this has no
// production caller today — kept for the dedicated unit tests that pin
// the rule independently of the UI (CreateApplication.test.js).
export function isCourseContextComplete(courseContext) {
    return getCourseContextErrors(courseContext).length === 0;
}

// Section 4: Support Rationale
function CreateApplicationSection4({ value, onChange, errorIds = new Set() }) {
    const hasError = (id) => errorIds.has(id);
    const groupInvalid = hasError('ca-support-use-cases-group');

    const toggleSupportUseCase = (optionValue) => {
        const current = value.supportUseCases || [];
        const isSelected = current.includes(optionValue);
        const next = isSelected
            ? current.filter((v) => v !== optionValue)
            : [...current, optionValue];
        const patch = { supportUseCases: next };
        // Clear the stale free-text answer once "Other" is deselected,
        // since the field is hidden (and no longer meaningful) after this.
        if (optionValue === 'other' && isSelected) {
            patch.otherSupportUseCase = '';
        }
        onChange(patch);
    };

    return (
        <div>
            <h2 className="h5 mb-3">Course Context</h2>
            <p className="text-muted mb-3">
                Help program coordinators understand why this course would
                benefit from peer support. This information is shared with each
                program you selected.
            </p>
            <Form.Group className="mb-3" controlId="ca-rationale-for-support">
                <Form.Label>
                    Rationale for Support <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Why does this course need peer support?"
                    value={value.rationaleForSupport}
                    onChange={(e) =>
                        onChange({ rationaleForSupport: e.target.value })
                    }
                    required
                    isInvalid={hasError('ca-rationale-for-support')}
                    aria-invalid={hasError('ca-rationale-for-support')}
                    aria-describedby={
                        hasError('ca-rationale-for-support')
                            ? 'ca-rationale-for-support-error'
                            : undefined
                    }
                />
                {hasError('ca-rationale-for-support') && (
                    <Form.Control.Feedback
                        type="invalid"
                        id="ca-rationale-for-support-error"
                        role="alert"
                    >
                        This field is required.
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <fieldset
                className={
                    groupInvalid
                        ? 'mb-3 border border-danger rounded p-2'
                        : 'mb-3'
                }
                id="ca-support-use-cases-group"
                tabIndex={-1}
                aria-invalid={groupInvalid}
                aria-describedby={
                    groupInvalid
                        ? 'ca-support-use-cases-group-error'
                        : undefined
                }
            >
                <Form.Label as="legend" className="float-none w-auto fs-6">
                    How will peer support help this course?{' '}
                    <span className="text-danger">*</span>
                </Form.Label>
                {SUPPORT_USE_CASE_OPTIONS.map((opt) => (
                    <Form.Check
                        key={opt.value}
                        type="checkbox"
                        id={`ca-support-use-case-${opt.value}`}
                        label={opt.label}
                        checked={(value.supportUseCases || []).includes(
                            opt.value
                        )}
                        onChange={() => toggleSupportUseCase(opt.value)}
                    />
                ))}
                {groupInvalid && (
                    <p
                        id="ca-support-use-cases-group-error"
                        className="text-danger small mb-0 mt-1"
                        role="alert"
                    >
                        Select at least one option.
                    </p>
                )}
                {(value.supportUseCases || []).includes('other') && (
                    <div className="mt-2">
                        <Form.Label htmlFor="ca-other-support-use-case">
                            Describe the other use case{' '}
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            id="ca-other-support-use-case"
                            type="text"
                            placeholder="e.g. peer tutoring in the help room"
                            value={value.otherSupportUseCase}
                            onChange={(e) =>
                                onChange({
                                    otherSupportUseCase: e.target.value,
                                })
                            }
                            required
                            isInvalid={hasError('ca-other-support-use-case')}
                            aria-invalid={hasError('ca-other-support-use-case')}
                            aria-describedby={
                                hasError('ca-other-support-use-case')
                                    ? 'ca-other-support-use-case-error'
                                    : undefined
                            }
                        />
                        {hasError('ca-other-support-use-case') && (
                            <Form.Control.Feedback
                                type="invalid"
                                id="ca-other-support-use-case-error"
                                role="alert"
                            >
                                This field is required.
                            </Form.Control.Feedback>
                        )}
                    </div>
                )}
            </fieldset>
            <Form.Group className="mb-3" controlId="ca-peer-supporter-benefit">
                <Form.Label>
                    How will the student who serves as a peer supporter benefit
                    from this experience? <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={value.peerSupporterBenefit}
                    onChange={(e) =>
                        onChange({ peerSupporterBenefit: e.target.value })
                    }
                    required
                    isInvalid={hasError('ca-peer-supporter-benefit')}
                    aria-invalid={hasError('ca-peer-supporter-benefit')}
                    aria-describedby={
                        hasError('ca-peer-supporter-benefit')
                            ? 'ca-peer-supporter-benefit-error'
                            : undefined
                    }
                />
                {hasError('ca-peer-supporter-benefit') && (
                    <Form.Control.Feedback
                        type="invalid"
                        id="ca-peer-supporter-benefit-error"
                        role="alert"
                    >
                        This field is required.
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            {/* Optional, so it has no entry in getCourseContextErrors. For
                special cases no other question covers, such as stacked
                sections (#459). */}
            <Form.Group className="mb-3" controlId="ca-additional-notes">
                <Form.Label>
                    Anything else we should know?{' '}
                    <span className="text-muted fw-normal">(optional)</span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="e.g. this section is stacked with another section, or a need specific to one program"
                    value={value.additionalNotes || ''}
                    onChange={(e) =>
                        onChange({ additionalNotes: e.target.value })
                    }
                    aria-describedby="ca-additional-notes-help"
                />
                <Form.Text id="ca-additional-notes-help" muted>
                    Every program coordinator reviewing this application will
                    see this.
                </Form.Text>
            </Form.Group>
        </div>
    );
}

export default CreateApplicationSection4;
